import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { SubmissionSummaryLogic } from './SubmissionSummaryLogic.ts';
import { SummarySignerEditableView } from './SummarySignerEditableView.tsx';
import { SummarySignerReadonlyView } from './SummarySignerReadonlyView.tsx';
import { useSubmissionSummaryData } from './useSubmissionSummaryData.ts';

export interface ISubmissionSummarySignerProps {
  submission?: RichiestaDTO | null;
  inquiryType?: TipoDomandaDTO | null;
  /**
   * Se `true`, mostra solo il download (senza possibilità di upload del
   * firmato). Vale per tutte le viste in sola lettura: Backoffice e Frontoffice
   * dopo l'invio della richiesta.
   */
  downloadOnly?: boolean;
  /**
   * Se `true`, la richiesta è completa e valida.
   * Generazione e firma sono possibili solo se la richiesta è completa.
   */
  isSubmissionComplete?: boolean;
}

/**
 * Componente per il download e upload del PDF di riepilogo firmato.
 * Usato sia in frontoffice che in backoffice.
 *
 * Visibilità:
 * - Disponibile solo con `modalitaRiepilogo = ESTESO`
 *
 * Comportamento:
 * - Sola lettura (`downloadOnly`: Backoffice o Frontoffice dopo l'invio): mostra `SummarySignerReadonlyView`, solo download del riepilogo (firmato se presente).
 * - Frontoffice: mostra `SummarySignerEditableView`, il cui comportamento dipende da
 *   `uploadEnabled` (derivato da `flAbilitaCaricamentoPdfRiepilogo` tramite `SubmissionSummaryLogic.isSignedSummaryUploadEnabled`):
 *     - `uploadEnabled = true`: download riepilogo + upload del firmato
 *     - `uploadEnabled = false`: solo download del riepilogo (non firmato)
 * - Generazione e firma possibili solo se la richiesta è completa e valida
 */
export function SubmissionSummarySigner(props: ISubmissionSummarySignerProps) {
  const { submission, inquiryType, downloadOnly, isSubmissionComplete } = props;

  const data = useSubmissionSummaryData({
    submission,
    inquiryType,
    isSubmissionComplete,
  });

  if (!(data.extendedSummaryEnabled && data.submissionId)) {
    return null;
  }

  const signatures = SubmissionSummaryLogic.resolveSignatures({
    inquiryType,
    hasSignedSummary: data.hasSignedSummary,
    signatures: submission?.riepilogoDettagliFirma,
  });

  if (downloadOnly) {
    return (
      <SummarySignerReadonlyView
        submissionId={data.submissionId}
        hasSignedSummary={data.hasSignedSummary}
        attachmentsError={data.attachmentsError}
        attachmentsLoading={data.attachmentsLoading}
        signedSummaryAttachment={data.signedSummaryAttachment}
        signatures={signatures}
      />
    );
  }

  return (
    <SummarySignerEditableView
      submissionId={data.submissionId}
      uploadEnabled={data.uploadEnabled}
      acceptedFileExtensions={data.acceptedFileExtensions}
      canInteract={data.canInteract}
      hasSignedSummary={data.hasSignedSummary}
      signedSummaryAttachment={data.signedSummaryAttachment}
      signatures={signatures}
    />
  );
}
